import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Download, Loader2, Paperclip, Trash2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { CommentsPanel, CommentBody } from '@/features/comments/CommentsPanel'
import { FrequencyContextPanel } from '@/shared/ui/frequency-context-panel'
import { t } from '@/shared/lib/i18n'
import { CONFIDENTIAL_MASK } from '@/shared/lib/confidential-mask'
import { getTaskLogStatusDisplay } from '@/shared/lib/taskLogStatusDisplay'
import { formatCalendarDate } from '@/shared/lib/tenantDateTime'
import apiClient from '@/shared/lib/apiClient'
import { instrumentReportsTaskLogDetailPath, instrumentTaskTaskLogPath } from '@/shared/lib/routes'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type { WebInstrumentTaskLogDetail } from '@/shared/types/web'
import {
  CommentLoggingContextProvider,
  useCommentLoggingContext,
  type CommentLoggingContext,
} from './commentLoggingContext'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

type TaskLogDetail = WebInstrumentTaskLogDetail

interface MissedCompliance {
  id: number
  dateIso: string
  taskName: string | null
  instrumentTaskId: number | null
  intervalTypeDescription: string | null
  reviewStatusCode: string | null
  reviewStatusLabel: string | null
  reviewerName: string | null
  reviewRecordedAtIso: string | null
  reviewComment: string | null
  resolutionTypeLabel: string | null
  completedByName: string | null
  completedAtLocal: string | null
  loggedAtLocal: string | null
}

type DayItem = { type: 'log'; data: TaskLogDetail } | { type: 'missed'; data: MissedCompliance }

// ── Props ───────────────────────────────────────────────────────────────────
interface Props {
  instrumentId: number
  /** The instrument_task id from the clicked cell — used for the destroy route and attachment URLs. */
  instrumentTaskId: number | null
  instrumentLabel?: string
  serialNumber?: string | null
  taskLogId: number | null
  /** Calendar date for which to load the day-list (ISO YYYY-MM-DD). */
  date?: string | null
  /** Window bounds for date-strip navigation (from_date / to_date). */
  fromDate?: string | null
  toDate?: string | null
  /**
   * Map of ISO date → first available task_log_id for that date.
   * Powers the date strip shown above the log list.
   */
  dateToLogIdMap?: Record<string, number>
  /** When provided, overrides the default `taskLogId != null` open guard. */
  open?: boolean
  /**
   * Scope the day list to unscheduled logs only. Set when opened from the aggregate
   * Unscheduled calendar row, whose cell carries no task id (legacy parity — the modal
   * must not leak every task's logs for that day).
   */
  unscheduledOnly?: boolean
  /**
   * Scope the day list to failure logs only (`qc_passed=false, is_npt=false`). Set when
   * opened from the Failure calendar row so that NT and Date-Modified-Complete entries
   * do not appear in the day list or date-strip navigation (AC1, AC3).
   */
  failureOnly?: boolean
  onClose: () => void
  /**
   * Called after a task log is successfully voided with the voided log id. Parents
   * refresh calendar/instrument props; Failures uses the id to drop the correct
   * entry from the remaining-ids list (day-strip can void a different log than the
   * one the Failures row originally opened).
   */
  onAfterDelete?: (deletedLogId: number) => void
}

type DayListState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; logs: TaskLogDetail[]; items: DayItem[] }

const dayOfMonth = (isoDate: string): string => {
  const day = Number(isoDate.split('-')[2])
  return String(day).padStart(2, '0')
}

// Pulls a human-readable message out of a failed void response. The backend returns
// { errors: { base: ["..."], <field>: ["..."] } } for guard failures (e.g. backdate past a
// department move -> 422); fall back to any first error string, then a generic message.
const extractVoidError = (error: unknown): string => {
  const errors = (error as { response?: { data?: { errors?: Record<string, string[]> } } })
    ?.response?.data?.errors
  if (errors) {
    const firstMessage = errors.base?.[0] ?? Object.values(errors).flat().find(Boolean)
    if (firstMessage) return firstMessage
  }
  return t('instruments.reports.logDetails.error')
}

const buildTaskLogsUrl = (
  instrumentId: number,
  date: string,
  fromDate?: string | null,
  toDate?: string | null,
  instrumentTaskId?: number | null,
  unscheduledOnly?: boolean,
  failureOnly?: boolean,
): string => {
  const base = `/instruments/${instrumentId}/reports/task_logs_by_date`
  const params = new URLSearchParams({ date })
  if (fromDate) params.set('from_date', fromDate)
  if (toDate) params.set('to_date', toDate)
  if (instrumentTaskId) params.set('instrument_task_id', String(instrumentTaskId))
  if (unscheduledOnly) params.set('unscheduled_only', '1')
  if (failureOnly) params.set('failure_only', '1')
  return `${base}?${params.toString()}`
}

type LogAttachment = TaskLogDetail['attachments'][number]

// Read-only file row for a File Attachment data field. Legacy rendered a "File Name" table with a
// download button under the field (REBUILD-380). The href is absent for ad-hoc logs with no task,
// since the download route is nested under one — show the name so the file is at least visible.
const AttachmentRow = ({ attachment }: { attachment: LogAttachment }) => {
  const name = attachment.attachmentName ?? '—'

  return (
    <div className="flex items-center justify-between gap-2 rounded border bg-background px-2 py-1">
      <span className="flex min-w-0 items-center gap-1.5">
        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 break-all">{name}</span>
      </span>
      {attachment.downloadHref && (
        <a
          href={attachment.downloadHref}
          download
          className="flex shrink-0 items-center gap-1 text-xs text-primary hover:underline"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          {t('instruments.reports.logDetails.download')}
        </a>
      )}
    </div>
  )
}

// Ad-hoc unscheduled logs carry no instrument_task, so they have no name. Legacy labelled
// them "Custom Task" in the calendar modal rather than leaving the row nameless
// (calendarView.template.html: instrumentTaskId === 0 && !instrumentTaskName).
export const logTaskLabel = (
  log: Pick<TaskLogDetail, 'taskName' | 'instrumentTaskId'>,
): string | null => {
  if (log.taskName) return log.taskName
  return log.instrumentTaskId == null ? t('instruments.reports.logDetails.customTask') : null
}

const mergeDayItems = (logs: TaskLogDetail[], missed: MissedCompliance[]): DayItem[] => {
  const logItems: DayItem[] = logs.map((data) => ({ type: 'log', data }))
  const missedItems: DayItem[] = missed.map((data) => ({ type: 'missed', data }))
  return [...missedItems, ...logItems].sort((a, b) => {
    const dateA = a.type === 'log' ? a.data.completedAtLocal : a.data.dateIso
    const dateB = b.type === 'log' ? b.data.completedAtLocal : b.data.dateIso
    return dateA.localeCompare(dateB)
  })
}

// ── Component ───────────────────────────────────────────────────────────────
const LogDetailsDialog = ({
  instrumentId,
  instrumentTaskId,
  instrumentLabel,
  serialNumber,
  taskLogId,
  date,
  fromDate,
  toDate,
  dateToLogIdMap,
  open,
  unscheduledOnly = false,
  failureOnly = false,
  onClose,
  onAfterDelete,
}: Props) => {
  const [dayListState, setDayListState] = useState<DayListState>({ status: 'idle' })
  const [currentDate, setCurrentDate] = useState<string | null>(date ?? null)
  // Set of expanded log ids — multiple rows may be open at once (parity with the occurrences
  // dialog). Auto-expansion only happens for a single-log day; otherwise rows start collapsed.
  const [expandedLogIds, setExpandedLogIds] = useState<Set<number>>(
    () => new Set(taskLogId != null ? [taskLogId] : []),
  )
  const isMountedRef = useRef(true)

  const canDeleteTaskLog = useCan(Resources.INSTRUMENT_TASK_LOG, Actions.DELETE)

  const [voidTargetLogId, setVoidTargetLogId] = useState<number | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [voiding, setVoiding] = useState(false)
  const [voidError, setVoidError] = useState<string | null>(null)
  // FallbackSingleLog fetches outside dayListState; remember each log's task id so void still
  // builds a correct destroy URL when the dialog prop instrumentTaskId is null (Failures path).
  const [fallbackTaskIdByLogId, setFallbackTaskIdByLogId] = useState<Record<number, number | null>>(
    {},
  )
  // REBUILD-531: open-logging context captured from the day-list / single-log fetch payloads.
  const [commentLoggingContext, setCommentLoggingContext] = useState<CommentLoggingContext>({
    logAuthenticationCode: null,
    loggerOptions: [],
    instrumentId,
  })

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleVoidRequest = (logId: number) => {
    setVoidTargetLogId(logId)
    setVoidReason('')
    setVoidError(null)
  }

  const registerFallbackLogTaskId = useCallback((logId: number, taskId: number | null) => {
    setFallbackTaskIdByLogId((previous) => {
      if (previous[logId] === taskId) return previous
      return { ...previous, [logId]: taskId }
    })
  }, [])

  const handleVoidCancel = () => {
    setVoidTargetLogId(null)
    setVoidReason('')
    setVoidError(null)
  }

  const handleVoidConfirm = (event: React.MouseEvent) => {
    // Radix AlertDialogAction closes the dialog on click by default; prevent that so a
    // failed void keeps the dialog open and surfaces the error (e.g. 422 backdate guard).
    event.preventDefault()
    if (voidTargetLogId == null) return

    // Legacy parity (sweetAlertWithDeleteLog inputValidator): a reason is mandatory. Block the
    // submit and surface the validation message instead of dispatching an empty-reason void.
    const trimmedReason = voidReason.trim()
    if (trimmedReason.length === 0) {
      setVoidError(t('instruments.reports.logDetails.voidReasonRequired'))
      return
    }

    // The day-strip pager can surface a log belonging to a different task than the
    // originally-clicked cell (navigation spans all of the instrument's logs). Void
    // against the voided log's OWN task id so the DELETE targets the correct task —
    // falling back to FallbackSingleLog's fetched task id, then the dialog prop.
    const logs = dayListState.status === 'loaded' ? dayListState.logs : []
    const targetTaskId =
      logs.find((log) => log.id === voidTargetLogId)?.instrumentTaskId ??
      fallbackTaskIdByLogId[voidTargetLogId] ??
      instrumentTaskId
    if (targetTaskId == null) {
      setVoidError(t('instruments.reports.logDetails.voidTaskIdMissing'))
      return
    }

    setVoiding(true)
    setVoidError(null)
    apiClient
      .delete(instrumentTaskTaskLogPath(instrumentId, targetTaskId), {
        data: { delete_reason: trimmedReason, instrument_task_log_id: voidTargetLogId },
      })
      .then(() => finishVoid())
      .catch((error) => {
        // A 404 means the log was already voided (e.g. a double-submit or a stale list).
        // Treat it as success: refresh so the stale row disappears instead of surfacing an error.
        if (error.response?.status === 404) {
          finishVoid()
          return
        }
        setVoiding(false)
        setVoidError(extractVoidError(error))
      })
  }

  // Refresh the open day list in place so the voided log disappears from the date-strip
  // navigation immediately; also let the parent refresh the calendar. Only close the dialog
  // when there is no day list to fall back to (single-log / deep-link open).
  // Defer the day-list refetch: Failures `onAfterDelete` clears parent state and unmounts this
  // dialog — avoid setState('loading') on a component that is about to unmount.
  const finishVoid = () => {
    const deletedLogId = voidTargetLogId
    setVoiding(false)
    setVoidTargetLogId(null)
    setVoidReason('')
    const dateToRefresh = currentDate
    // Pass the voided id (not the originally opened taskLogId) so Failures can filter
    // remaining ids correctly when the day strip voided a different log.
    if (deletedLogId != null) {
      onAfterDelete?.(deletedLogId)
    }
    if (dateToRefresh) {
      setTimeout(() => {
        if (!isMountedRef.current) return
        fetchDayList(dateToRefresh)
      }, 0)
    } else {
      onClose()
    }
  }

  const fetchDayList = (targetDate: string, expandId?: number | null) => {
    setDayListState({ status: 'loading' })
    apiClient
      .get<{
        taskLogs: TaskLogDetail[]
        missedCompliances: MissedCompliance[]
        logAuthenticationCode?: string | null
        loggerOptions?: { id: number; label: string }[]
      }>(
        // No instrument_task_id scope: the date-strip pager spans ALL of the instrument's
        // logs (legacy parity), so the day list must include logs from every task — otherwise
        // a cross-task log surfaced by the strip is absent here and voiding it targets the
        // wrong task (→ 404). See handleVoidConfirm, which reads each log's own task id.
        // failureOnly is forwarded so date-strip navigation stays scoped to failure logs
        // when opened from the Failure calendar row (AC3).
        buildTaskLogsUrl(
          instrumentId,
          targetDate,
          fromDate,
          toDate,
          null,
          unscheduledOnly,
          failureOnly,
        ),
      )
      .then((res) => {
        // Failures onAfterDelete can unmount this dialog before the in-flight day list returns.
        if (!isMountedRef.current) return
        const logs = res.data.taskLogs ?? []
        const missed = res.data.missedCompliances ?? []
        const items = mergeDayItems(logs, missed)
        setDayListState({ status: 'loaded', logs, items })
        setCommentLoggingContext({
          logAuthenticationCode: res.data.logAuthenticationCode ?? null,
          loggerOptions: res.data.loggerOptions ?? [],
          instrumentId,
        })
        // Auto-expand only a single-log day (parity with the occurrences dialog: >1 rows start
        // collapsed). A directly requested log (entry point / strip click on a specific date)
        // opens when it belongs to this day; otherwise every row stays collapsed and the user
        // expands via the left chevron.
        const nextExpanded = new Set<number>()
        if (logs.length === 1) {
          nextExpanded.add(logs[0].id)
        } else if (expandId != null && logs.some((log) => log.id === expandId)) {
          nextExpanded.add(expandId)
        }
        setExpandedLogIds(nextExpanded)
      })
      .catch(() => {
        if (!isMountedRef.current) return
        setDayListState({ status: 'error' })
      })
  }

  const isOpen = open ?? taskLogId != null

  useEffect(() => {
    if (!isOpen) {
      setDayListState({ status: 'idle' })
      setCurrentDate(null)
      setExpandedLogIds(new Set())
      return
    }
    if (taskLogId != null) {
      const targetDate = date ?? null
      if (targetDate) {
        setCurrentDate(targetDate)
        fetchDayList(targetDate, taskLogId)
      } else {
        // No date provided: fall back to fetching single log detail
        setCurrentDate(null)
        setExpandedLogIds(new Set(taskLogId != null ? [taskLogId] : []))
        setDayListState({ status: 'idle' })
      }
    } else if (date != null) {
      // date-only mode: triggered when open=true but taskLogId is null
      setCurrentDate(date)
      fetchDayList(date, null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, taskLogId, date])

  const handleDateStripClick = (stripDate: string, logId: number) => {
    setCurrentDate(stripDate)
    fetchDayList(stripDate, logId)
  }
  const headerTitle = instrumentLabel
    ? serialNumber
      ? `${instrumentLabel} (${serialNumber})`
      : instrumentLabel
    : ''
  const logs = dayListState.status === 'loaded' ? dayListState.logs : []
  const items = dayListState.status === 'loaded' ? dayListState.items : []
  // The fallback single-log fetch and the "not found" branch below still need a single id
  // (the entry point / strip target); the multi-expand set is only meaningful for the list.
  const firstExpandedLogId = expandedLogIds.values().next().value ?? null

  const toggleExpandedLog = (logId: number) => {
    setExpandedLogIds((previous) => {
      const next = new Set(previous)
      if (next.has(logId)) {
        next.delete(logId)
      } else {
        next.add(logId)
      }
      return next
    })
  }

  return (
    <CommentLoggingContextProvider.Provider value={commentLoggingContext}>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            {/* Radix requires a DialogTitle for the dialog's accessible name. Openers without an
                instrument label (History-tab task history) fall back to a visually hidden one. */}
            {headerTitle ? (
              <DialogTitle className="text-sm font-normal text-muted-foreground">
                {headerTitle}
              </DialogTitle>
            ) : (
              <DialogTitle className="sr-only">
                {t('instruments.reports.logDetails.title')}
              </DialogTitle>
            )}
            <p className="text-base font-semibold">{t('instruments.reports.logDetails.title')}</p>
          </DialogHeader>

          {/* Date strip — shows all dates with logs in the current window */}
          {dateToLogIdMap && Object.keys(dateToLogIdMap).length > 0 && (
            <div className="flex flex-wrap gap-1 border-b pb-2">
              {Object.entries(dateToLogIdMap)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([stripDate, logId]) => {
                  const isActive = currentDate === stripDate
                  return (
                    <button
                      key={stripDate}
                      type="button"
                      className={[
                        'rounded px-1.5 py-0.5 text-xs transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground font-semibold'
                          : 'bg-muted text-muted-foreground hover:bg-muted/70',
                      ].join(' ')}
                      onClick={() => handleDateStripClick(stripDate, logId)}
                      aria-label={t('instruments.reports.logDetails.dateStripAria', {
                        day: dayOfMonth(stripDate),
                      })}
                      aria-pressed={isActive}
                    >
                      {dayOfMonth(stripDate)}
                    </button>
                  )
                })}
            </div>
          )}

          {/* Body */}
          <div className="min-w-0 space-y-2">
            {dayListState.status === 'loading' && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {dayListState.status === 'error' && (
              <div className="space-y-3 py-6 text-center">
                <p className="text-sm text-destructive">
                  {t('instruments.reports.logDetails.error')}
                </p>
                <Button variant="outline" size="sm" onClick={onClose}>
                  {t('instruments.reports.logDetails.close')}
                </Button>
              </div>
            )}

            {dayListState.status === 'idle' && taskLogId != null && !date && (
              <FallbackSingleLog
                instrumentId={instrumentId}
                instrumentTaskId={instrumentTaskId}
                taskLogId={taskLogId}
                fromDate={fromDate}
                toDate={toDate}
                canDeleteTaskLog={canDeleteTaskLog}
                onDeleteLog={handleVoidRequest}
                onDetailLoaded={registerFallbackLogTaskId}
                onClose={onClose}
              />
            )}

            {/* Day list is empty but we have a known log ID (e.g. a backdated/date_modified log
              whose completed_at is on a different day than the calendar date). Fall back to the
              single-log fetch so the user still sees the entry. */}
            {dayListState.status === 'loaded' &&
              items.length === 0 &&
              firstExpandedLogId != null && (
                <FallbackSingleLog
                  instrumentId={instrumentId}
                  instrumentTaskId={instrumentTaskId}
                  taskLogId={firstExpandedLogId}
                  fromDate={fromDate}
                  toDate={toDate}
                  canDeleteTaskLog={canDeleteTaskLog}
                  onDeleteLog={handleVoidRequest}
                  onDetailLoaded={registerFallbackLogTaskId}
                  onClose={onClose}
                />
              )}

            {dayListState.status === 'loaded' &&
              items.length === 0 &&
              logs.length === 0 &&
              firstExpandedLogId == null && (
                <div className="space-y-3 py-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t('instruments.reports.logDetails.notFound')}
                  </p>
                  <Button variant="outline" size="sm" onClick={onClose}>
                    {t('instruments.reports.logDetails.cancel')}
                  </Button>
                </div>
              )}

            {dayListState.status === 'loaded' && items.length > 0 && (
              <DayItemList
                items={items}
                expandedLogIds={expandedLogIds}
                instrumentId={instrumentId}
                instrumentTaskId={instrumentTaskId}
                canDeleteTaskLog={canDeleteTaskLog}
                onToggleExpand={toggleExpandedLog}
                onDayListRefresh={() =>
                  currentDate && fetchDayList(currentDate, firstExpandedLogId)
                }
                onDeleteLog={handleVoidRequest}
              />
            )}
          </div>

          {/* Footer — only show when DayLogList (multi-log view) is active */}
          {dayListState.status === 'loaded' && logs.length > 0 && (
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" size="sm" onClick={onClose}>
                {t('instruments.reports.logDetails.cancel')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Void confirmation dialog */}
      <AlertDialog
        open={voidTargetLogId != null}
        onOpenChange={(open) => !open && handleVoidCancel()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('instruments.reports.logDetails.voidDialogTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('instruments.reports.logDetails.voidDialogDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="void-reason-dialog">
              {t('instruments.reports.logDetails.voidReasonLabel')}
            </Label>
            <Textarea
              id="void-reason-dialog"
              rows={3}
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
              placeholder={t('instruments.reports.logDetails.voidReasonPlaceholder')}
              disabled={voiding}
            />
            {voidError && <p className="text-sm text-destructive">{voidError}</p>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={voiding}>
              {t('instruments.reports.logDetails.voidCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVoidConfirm}
              disabled={voiding || voidReason.trim().length === 0}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {voiding
                ? t('instruments.reports.logDetails.voidSubmitting')
                : t('instruments.reports.logDetails.voidConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CommentLoggingContextProvider.Provider>
  )
}

// ── DayItemList ──────────────────────────────────────────────────────────────

interface DayItemListProps {
  items: DayItem[]
  expandedLogIds: Set<number>
  instrumentId: number
  instrumentTaskId: number | null
  canDeleteTaskLog: boolean
  onToggleExpand: (id: number) => void
  onDayListRefresh: () => void
  onDeleteLog?: (logId: number) => void
}

const DayItemList = ({
  items,
  expandedLogIds,
  instrumentId,
  instrumentTaskId,
  canDeleteTaskLog,
  onToggleExpand,
  onDayListRefresh,
  onDeleteLog,
}: DayItemListProps) => (
  <div className="divide-y rounded border">
    {items.map((item) => {
      if (item.type === 'missed') {
        return <MissedDateRow key={`missed-${item.data.id}`} record={item.data} />
      }
      const log = item.data
      return (
        <DayLogRow
          key={log.id}
          log={log}
          isExpanded={expandedLogIds.has(log.id)}
          instrumentId={instrumentId}
          instrumentTaskId={instrumentTaskId}
          canDeleteTaskLog={canDeleteTaskLog}
          onToggle={() => onToggleExpand(log.id)}
          onCommentChange={onDayListRefresh}
          onDeleteLog={onDeleteLog}
        />
      )
    })}
  </div>
)

// ── MissedDateRow ────────────────────────────────────────────────────────────

const MissedDateRow = ({ record }: { record: MissedCompliance }) => {
  const { formatWallClockDisplay } = useSiteDateTime()
  const isReviewed = record.reviewStatusCode != null
  const statusLabel = isReviewed
    ? (record.reviewStatusLabel ?? record.reviewStatusCode)
    : t('instruments.reports.logDetails.missedDate')
  const statusClass = isReviewed
    ? 'bg-amber-100 text-amber-800 border-amber-200'
    : 'bg-red-100 text-red-700 border-red-200'
  const hasResolutionFacts =
    record.completedByName != null ||
    record.completedAtLocal != null ||
    record.loggedAtLocal != null

  return (
    <div className="px-3 py-2 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant="outline" className={`text-xs ${statusClass}`}>
            {statusLabel}
          </Badge>
          {record.taskName && (
            <span className="text-muted-foreground truncate max-w-[200px]">{record.taskName}</span>
          )}
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatCalendarDate(record.dateIso)}
        </span>
      </div>
      {record.intervalTypeDescription && (
        <p className="text-xs text-muted-foreground pl-1">{record.intervalTypeDescription}</p>
      )}
      {/* Review attribution — kept in its OWN row, distinct from resolution facts. */}
      {isReviewed && record.reviewerName && (
        <p className="text-xs text-muted-foreground pl-1">
          {t('instruments.reports.resolvedDetail.reviewedBy')}: {record.reviewerName}
          {record.reviewRecordedAtIso && ` — ${formatWallClockDisplay(record.reviewRecordedAtIso)}`}
        </p>
      )}
      {/* REBUILD-607: the explanation entered for the NC confirmation / override (legacy label). */}
      {record.reviewComment && (
        <div className="pl-1 text-xs">
          <span className="font-medium text-muted-foreground">
            {t('instruments.reports.logDetails.explanationLabel')}:
          </span>
          <CommentBody body={record.reviewComment} />
        </div>
      )}
      {/* Resolution type — the canonical action label, distinct from the review row. */}
      {record.resolutionTypeLabel && (
        <p className="text-xs text-muted-foreground pl-1">
          {t('instruments.reports.resolvedDetail.resolutionType')}: {record.resolutionTypeLabel}
        </p>
      )}
      {/* Resolution facts — performing user + BOTH timestamps, mirroring LogDetailBody. */}
      {hasResolutionFacts && (
        <div className="pl-1 text-xs text-muted-foreground">
          {record.completedByName && (
            <p>
              {t('instruments.reports.resolvedDetail.performedBy')}: {record.completedByName}
            </p>
          )}
          {record.completedAtLocal && (
            <p>
              {t('instruments.reports.resolvedDetail.performedAt')}:{' '}
              {formatWallClockDisplay(record.completedAtLocal)}
            </p>
          )}
          {record.loggedAtLocal && (
            <p>
              {t('instruments.reports.resolvedDetail.loggedAt')}:{' '}
              {formatWallClockDisplay(record.loggedAtLocal)}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ── DayLogRow ────────────────────────────────────────────────────────────────

interface DayLogRowProps {
  log: TaskLogDetail
  isExpanded: boolean
  instrumentId: number
  instrumentTaskId: number | null
  canDeleteTaskLog: boolean
  onToggle: () => void
  onCommentChange: () => void
  onDeleteLog?: (logId: number) => void
}

const DayLogRow = ({
  log,
  isExpanded,
  instrumentId,
  instrumentTaskId,
  canDeleteTaskLog,
  onToggle,
  onCommentChange,
  onDeleteLog,
}: DayLogRowProps) => {
  const { formatWallClockDisplay } = useSiteDateTime()
  const commentLogging = useCommentLoggingContext()
  const [showComments, setShowComments] = useState(false)
  // REBUILD-498: gate the Add-Comment affordance on the same COMMENTS/create permission the
  // CommentsPanel form uses, so the "User" role never opens an empty "No comment" form.
  const canCreateComment = useCan(Resources.COMMENTS, Actions.CREATE)

  const { label: statusLabel, className: statusClass } = getTaskLogStatusDisplay(log)
  const chipTaskLabel = logTaskLabel(log)

  return (
    <div>
      {/* Collapsed header — always visible. Chevron on the left mirrors the occurrences dialog. */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge
            variant="outline"
            className={`text-xs ${statusClass}`}
            data-testid="log-details-status"
          >
            {statusLabel}
          </Badge>
          {chipTaskLabel && (
            <span className="text-muted-foreground truncate max-w-[200px]">{chipTaskLabel}</span>
          )}
          <span className="text-xs text-muted-foreground">
            {formatWallClockDisplay(log.completedAtLocal)}
          </span>
        </div>
      </button>

      {/* Expanded body */}
      {isExpanded && (
        <div className="border-t bg-muted/10 px-3 pb-3 pt-2 space-y-3">
          <LogDetailBody
            detail={log}
            instrumentId={instrumentId}
            instrumentTaskId={instrumentTaskId}
            canDeleteTaskLog={canDeleteTaskLog}
            onDeleteLog={onDeleteLog}
            hideStatusBadge
          />
          {!log.isNpt && (showComments || canCreateComment) && (
            <div className="space-y-2 border-t pt-2">
              {showComments && (
                <CommentsPanel
                  comments={log.comments}
                  commentableType="InstrumentTaskLog"
                  commentableId={log.id}
                  logAuthenticationCode={commentLogging.logAuthenticationCode}
                  loggerOptions={commentLogging.loggerOptions}
                  instrumentId={commentLogging.instrumentId}
                  onCommentChange={() => {
                    onCommentChange()
                    setShowComments(false)
                  }}
                />
              )}
              {!showComments && canCreateComment && (
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowComments(true)}>
                    {t('instruments.reports.logDetails.addComment')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── FallbackSingleLog — used when no date is provided (deep-link / backward compat) ──

interface FallbackSingleLogProps {
  instrumentId: number
  instrumentTaskId: number | null
  taskLogId: number
  fromDate?: string | null
  toDate?: string | null
  canDeleteTaskLog: boolean
  onDeleteLog?: (logId: number) => void
  /** Registers the fetched log's task id so parent void can build the destroy URL. */
  onDetailLoaded?: (logId: number, instrumentTaskId: number | null) => void
  onClose: () => void
}

const FallbackSingleLog = ({
  instrumentId,
  instrumentTaskId: _instrumentTaskId,
  taskLogId,
  fromDate: _fromDate,
  toDate: _toDate,
  canDeleteTaskLog,
  onDeleteLog,
  onDetailLoaded,
  onClose,
}: FallbackSingleLogProps) => {
  const { formatWallClockDisplay } = useSiteDateTime()
  const [loadState, setLoadState] = useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'not_found' }
    | { status: 'loaded'; detail: TaskLogDetail }
  >({ status: 'loading' })
  const [showComments, setShowComments] = useState(false)
  // Single log: open expanded (parity with a one-log day in the Reports-tab list) so its data fields
  // and attachments are visible without an extra click — the History tab opens this path.
  const [isExpanded, setIsExpanded] = useState(true)
  const [commentLoggingContext, setCommentLoggingContext] = useState<CommentLoggingContext>({
    logAuthenticationCode: null,
    loggerOptions: [],
    instrumentId,
  })
  // REBUILD-498: gate the Add-Comment affordance on COMMENTS/create so the "User" role
  // cannot open an empty comment form.
  const canCreateComment = useCan(Resources.COMMENTS, Actions.CREATE)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    apiClient
      .get<{
        reportsTaskLogDetail: TaskLogDetail
        logAuthenticationCode?: string | null
        loggerOptions?: { id: number; label: string }[]
      }>(instrumentReportsTaskLogDetailPath(instrumentId, taskLogId, {}))
      .then((res) => {
        if (!isMountedRef.current) return
        setCommentLoggingContext({
          logAuthenticationCode: res.data.logAuthenticationCode ?? null,
          loggerOptions: res.data.loggerOptions ?? [],
          instrumentId,
        })
        const detail = res.data.reportsTaskLogDetail
        onDetailLoaded?.(detail.id, detail.instrumentTaskId)
        setLoadState({ status: 'loaded', detail })
      })
      .catch((error) => {
        if (!isMountedRef.current) return
        if (error.response?.status === 404) {
          setLoadState({ status: 'not_found' })
        } else {
          setLoadState({ status: 'error' })
        }
      })
  }, [instrumentId, taskLogId, onDetailLoaded])

  if (loadState.status === 'loading') {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (loadState.status === 'not_found') {
    return (
      <div className="space-y-3 py-6 text-center">
        <p className="text-sm text-muted-foreground">
          {t('instruments.reports.logDetails.notFound')}
        </p>
        <Button variant="outline" size="sm" onClick={onClose}>
          {t('instruments.reports.logDetails.cancel')}
        </Button>
      </div>
    )
  }

  if (loadState.status === 'error') {
    return (
      <div className="space-y-3 py-6 text-center">
        <p className="text-sm text-destructive">{t('instruments.reports.logDetails.error')}</p>
        <Button variant="outline" size="sm" onClick={onClose}>
          {t('instruments.reports.logDetails.close')}
        </Button>
      </div>
    )
  }

  const { detail } = loadState

  const refetch = () => {
    apiClient
      .get<{ reportsTaskLogDetail: TaskLogDetail }>(
        instrumentReportsTaskLogDetailPath(instrumentId, detail.id, {}),
      )
      .then((res) => {
        if (!isMountedRef.current) return
        setLoadState({ status: 'loaded', detail: res.data.reportsTaskLogDetail })
      })
      .catch(() => {})
  }

  const { label: statusLabel, className: statusClass } = getTaskLogStatusDisplay(detail)
  const identityLabel = [
    detail.completedByName,
    detail.completedByUserId != null ? `(ID:${detail.completedByUserId})` : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <CommentLoggingContextProvider.Provider value={commentLoggingContext}>
      <div className="space-y-3">
        <div className="rounded border">
          {/* Accordion header — mirrors DayLogRow collapsed header */}
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/50"
            onClick={() => setIsExpanded((previous) => !previous)}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="outline" className={`text-xs ${statusClass}`}>
                {statusLabel}
              </Badge>
              {identityLabel && <span className="text-muted-foreground">{identityLabel}</span>}
              <span className="text-xs text-muted-foreground">
                {formatWallClockDisplay(detail.completedAtLocal)}
              </span>
            </div>
          </button>

          {/* Expanded body — delete icon appears inline on the "Complete:" row */}
          {isExpanded && (
            <div className="border-t bg-muted/10 px-3 pb-3 pt-2 space-y-3">
              <LogDetailBody
                detail={detail}
                instrumentId={instrumentId}
                instrumentTaskId={_instrumentTaskId}
                canDeleteTaskLog={canDeleteTaskLog}
                showComments={showComments}
                onDeleteLog={onDeleteLog}
                hideStatusBadge
                onCommentChange={() => {
                  refetch()
                  setShowComments(false)
                }}
              />
              {!detail.isNpt && !showComments && canCreateComment && (
                <div className="flex justify-end border-t pt-2">
                  <Button variant="outline" size="sm" onClick={() => setShowComments(true)}>
                    {t('instruments.reports.logDetails.addComment')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('instruments.reports.logDetails.cancel')}
          </Button>
        </div>
      </div>
    </CommentLoggingContextProvider.Provider>
  )
}

// ── LotBucketsSection — renders current lots + used lots (legacy two-bucket display) ──

const LotBucketsSection = ({ detail }: { detail: TaskLogDetail }) => {
  const { currentLots, usedLots } = detail
  const hasCurrentLots = currentLots.length > 0
  const hasUsedLots = usedLots.length > 0

  if (!hasCurrentLots && !hasUsedLots) {
    return null
  }

  const usedLotIds = new Set(usedLots.map((lot) => lot.lotId).filter(Boolean))
  const deduplicatedCurrentLots =
    usedLotIds.size > 0 ? currentLots.filter((lot) => !usedLotIds.has(lot.lotId)) : currentLots

  return (
    <div className="space-y-3">
      {deduplicatedCurrentLots.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold text-muted-foreground">
            {t('instruments.reports.logDetails.currentLots')}
          </h4>
          <div className="space-y-1">
            {deduplicatedCurrentLots.map((lot) => (
              <LotRow
                key={`current-${lot.id}`}
                lotName={lot.lotName}
                lotNumber={lot.lotNumber}
                manufacturer={lot.manufacturer}
                product={lot.product}
                expirationDate={lot.openedExpiration ?? lot.expirationDate}
                expiresSoon={lot.expiresSoon}
                detail={{
                  lotNumber: lot.lotNumber,
                  expirationDate: lot.expirationDate,
                  openedExpiration: lot.openedExpiration,
                  otherIdentifier: lot.otherIdentifier,
                  manufacturer: lot.manufacturer,
                  description: lot.description,
                  dateReceived: lot.dateReceived,
                  dateOpened: lot.dateOpened,
                  dateInUse: lot.dateInUse,
                  dateDiscarded: lot.dateDiscarded,
                  product: lot.product,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {hasUsedLots && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold text-muted-foreground">
            {t('instruments.reports.logDetails.lotsUsed')}
          </h4>
          <div className="space-y-1">
            {usedLots.map((lot) => (
              <LotRow
                key={`used-${lot.id}`}
                lotName={lot.lotName}
                lotNumber={lot.lotNumber}
                manufacturer={lot.manufacturer}
                product={lot.product}
                expirationDate={lot.expirationDate}
                expiresSoon={lot.expiresSoon}
                detail={{
                  lotNumber: lot.lotNumber,
                  expirationDate: lot.expirationDate,
                  openedExpiration: lot.openedExpiration,
                  otherIdentifier: lot.otherIdentifier,
                  manufacturer: lot.manufacturer,
                  description: lot.description,
                  dateReceived: lot.dateReceived,
                  dateOpened: lot.dateOpened,
                  dateInUse: lot.dateInUse,
                  dateDiscarded: lot.dateDiscarded,
                  product: lot.product,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface LotRowProps {
  lotName: string | null
  lotNumber: string | null
  manufacturer: string | null
  product: string | null
  expirationDate: string | null
  expiresSoon: boolean
  /** Full lot-detail fields shown when the row is expanded (legacy "Hide Details" panel). */
  detail: LotDetailFields
}

/** Full lot-detail fields rendered inside the expandable panel, mirroring the legacy
 *  instrumentTasksDetail two-column layout (REBUILD-411). */
interface LotDetailFields {
  lotNumber: string | null
  expirationDate: string | null
  openedExpiration: string | null
  otherIdentifier: string | null
  manufacturer: string | null
  description: string | null
  dateReceived: string | null
  dateOpened: string | null
  dateInUse: string | null
  dateDiscarded: string | null
  product: string | null
}

const formatLotDate = (value: string | null): string => (value ? formatCalendarDate(value) : '')

const LotRow = ({
  lotName,
  lotNumber,
  manufacturer,
  product,
  expirationDate,
  expiresSoon,
  detail,
}: LotRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const expirationDisplay = expirationDate
    ? `${t('instruments.reports.logDetails.expires')} ${formatCalendarDate(expirationDate)}`
    : t('instruments.reports.logDetails.noExpiration')

  const leftColumn: Array<[string, string]> = [
    [t('instruments.reports.logDetails.lotDetail.lotNumber'), detail.lotNumber ?? ''],
    [
      t('instruments.reports.logDetails.lotDetail.expirationDate'),
      detail.expirationDate
        ? formatLotDate(detail.expirationDate)
        : t('instruments.reports.logDetails.lotDetail.noExpiration'),
    ],
    [
      t('instruments.reports.logDetails.lotDetail.openedExpiration'),
      formatLotDate(detail.openedExpiration),
    ],
    [t('instruments.reports.logDetails.lotDetail.otherIdentifier'), detail.otherIdentifier ?? ''],
    [t('instruments.reports.logDetails.lotDetail.manufacturer'), detail.manufacturer ?? ''],
    [t('instruments.reports.logDetails.lotDetail.description'), detail.description ?? ''],
  ]
  const rightColumn: Array<[string, string]> = [
    [
      t('instruments.reports.logDetails.lotDetail.dateReceived'),
      formatLotDate(detail.dateReceived),
    ],
    [t('instruments.reports.logDetails.lotDetail.dateOpened'), formatLotDate(detail.dateOpened)],
    [t('instruments.reports.logDetails.lotDetail.dateInUse'), formatLotDate(detail.dateInUse)],
    [
      t('instruments.reports.logDetails.lotDetail.dateDiscarded'),
      formatLotDate(detail.dateDiscarded),
    ],
    [t('instruments.reports.logDetails.lotDetail.product'), detail.product ?? ''],
  ]

  return (
    <div className="rounded bg-muted px-3 py-2 text-xs">
      <button
        type="button"
        className="flex w-full flex-col gap-1 text-left"
        aria-expanded={isExpanded}
        aria-label={lotName ?? lotNumber ?? t('instruments.reports.logDetails.currentLots')}
        onClick={() => setIsExpanded((open) => !open)}
      >
        <div className="flex items-start gap-2">
          {isExpanded ? (
            <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
            {lotName && <span className="font-medium break-words">{lotName}</span>}
            {lotNumber && (
              <span className="ml-2 break-words text-muted-foreground">{lotNumber}</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-6">
          <span className={expiresSoon ? 'font-medium text-amber-700' : 'text-muted-foreground'}>
            {expirationDisplay}
          </span>
          {expiresSoon && (
            <span className="text-amber-600">
              ⚠ {t('instruments.reports.logDetails.lotsExpireAlert')}
            </span>
          )}
        </div>
      </button>

      {!isExpanded && (manufacturer || product) && (
        <div className="mt-1 break-words pl-6 text-muted-foreground">
          {[manufacturer, product].filter(Boolean).join(' — ')}
        </div>
      )}

      {isExpanded && (
        <div className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2 pl-6 sm:grid-cols-2">
          <div className="space-y-2">
            {leftColumn.map(([label, value]) => (
              <div key={label} className="flex flex-col">
                <span className="font-medium">{label}</span>
                <span className="break-words text-muted-foreground">{value || '—'}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {rightColumn.map(([label, value]) => (
              <div key={label} className="flex flex-col">
                <span className="font-medium">{label}</span>
                <span className="break-words text-muted-foreground">{value || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Log detail body ──────────────────────────────────────────────────────────
export interface LogDetailBodyProps {
  detail: TaskLogDetail
  instrumentId: number
  instrumentTaskId: number | null
  /** When true the delete (void) button is shown; controlled by the parent's RBAC check. */
  canDeleteTaskLog?: boolean
  onDeleteLog?: (logId: number) => void
  showComments?: boolean
  onCommentChange?: () => void
  /**
   * When true, omit the completed-by / completed-at identity row. Used by the calendar
   * day-occurrences modal, which already shows that text next to the status badge.
   */
  hideIdentityRow?: boolean
  /**
   * When true, suppress the inner status badge header row. Used when the embedding
   * occurrence row already renders the status badge, to avoid a duplicate action/status
   * label (REBUILD-146 AC8). Task name still renders inside the detail card (E50-S4).
   */
  hideStatusBadge?: boolean
}

export const LogDetailBody = ({
  detail,
  instrumentId: _instrumentId,
  instrumentTaskId: _instrumentTaskId,
  canDeleteTaskLog = false,
  onDeleteLog,
  showComments,
  onCommentChange,
  hideIdentityRow = false,
  hideStatusBadge = false,
}: LogDetailBodyProps) => {
  const { formatWallClockDisplay } = useSiteDateTime()
  const commentLogging = useCommentLoggingContext()

  const { label: statusLabel, className: statusClass } = getTaskLogStatusDisplay(detail)
  const taskLabel = logTaskLabel(detail)

  // Data-field files render inline under their field; anything left over gets its own block.
  const pairedFieldIds = new Set(
    detail.isNpt
      ? []
      : detail.dataFieldLogs
          .map((dfl) => dfl.instrumentTaskDataFieldId)
          .filter((id): id is number => id != null),
  )
  const unpairedAttachments = detail.attachments.filter(
    (attachment) =>
      attachment.instrumentTaskDataFieldId == null ||
      !pairedFieldIds.has(attachment.instrumentTaskDataFieldId),
  )

  // Wastebin (REBUILD-374) must survive even when the embedding row hides the badge/toggle
  // (REBUILD-146 AC8). When the header would otherwise be empty (embedded flat view), render the
  // wastebin inline on the "Complete:" line instead of a standalone row that looks empty.
  const canShowDelete = canDeleteTaskLog && !!onDeleteLog && detail.deletable
  const headerHasContent = !hideIdentityRow || !hideStatusBadge
  const showHeaderRow = headerHasContent
  const deleteInHeader = canShowDelete && headerHasContent
  const deleteInline = canShowDelete && !headerHasContent
  // DayLogRow / day-occurrences pass hideStatusBadge to avoid a duplicate Failure/Complete
  // pill. Task name must still appear inside the detail card (E50-S4 / UAT failure-details
  // parity) — not only on the muted accordion chip or dialog title.
  const showTaskLabelInBody = Boolean(hideStatusBadge && taskLabel)

  return (
    <div className="text-sm">
      {/* Log entry card — thin border like legacy */}
      <div className="rounded border px-3 py-2">
        {showHeaderRow && (
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              {!hideIdentityRow && (
                <div className="flex flex-wrap items-center gap-x-2">
                  <span className="font-medium">
                    {detail.completedByName ?? '—'}
                    {detail.completedByUserId != null ? ` (ID:${detail.completedByUserId})` : ''}
                  </span>
                  <span className="text-muted-foreground">|</span>
                  <span>{formatWallClockDisplay(detail.completedAtLocal)}</span>
                </div>
              )}
              {!hideStatusBadge && (
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    className={`text-xs ${statusClass}`}
                    data-testid="log-details-status"
                  >
                    {statusLabel}
                  </Badge>
                  {taskLabel && (
                    <span className="text-muted-foreground" data-testid="log-details-task-name">
                      | {taskLabel}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="ml-2 flex shrink-0 items-center gap-2">
              {deleteInHeader && (
                <button
                  type="button"
                  className="text-destructive hover:text-destructive/80"
                  onClick={() => onDeleteLog!(detail.id)}
                  title={t('instruments.reports.logDetails.voidLog')}
                  aria-label={t('instruments.reports.logDetails.voidLog')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}

        <div
          className={['space-y-3 rounded bg-muted/40 px-3 py-2', showHeaderRow ? 'mt-3' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {showTaskLabelInBody && (
            <div className="text-muted-foreground" data-testid="log-details-task-name">
              {taskLabel}
            </div>
          )}
          <div className="flex items-start justify-between gap-2">
            <div>
              {t('instruments.reports.logDetails.complete')}:{' '}
              {formatWallClockDisplay(detail.completedAtLocal)}
            </div>
            {deleteInline && (
              <button
                type="button"
                className="shrink-0 text-destructive hover:text-destructive/80"
                onClick={() => onDeleteLog!(detail.id)}
                title={t('instruments.reports.logDetails.voidLog')}
                aria-label={t('instruments.reports.logDetails.voidLog')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>

          {(detail.intervalTypeDescription || detail.frequencyTypeCode) && (
            <FrequencyContextPanel
              frequencyTypeCode={detail.frequencyTypeCode}
              intervalTypeDescription={detail.intervalTypeDescription}
              frequencyRrule={detail.frequencyRrule}
              leadTimeIntervalHours={detail.leadTimeHours}
              leadTimeUnit={detail.leadTimeUnit}
              lateTimeIntervalHours={detail.overdueHours}
              lateTimeUnit={detail.lateTimeUnit}
              fixedScheduleStartTime={detail.fixedScheduleStartTime}
              endsAtMidnight={detail.endsAtMidnight}
            />
          )}

          {/* Data fields */}
          {!detail.isNpt && detail.dataFieldLogs.length > 0 && (
            <div className="space-y-1">
              {detail.dataFieldLogs.map((dfl) => {
                const isOutOfRange =
                  !dfl.isConfidentialHidden && dfl.requiresValidation && !dfl.acceptableValue
                const fieldAttachments =
                  dfl.instrumentTaskDataFieldId == null
                    ? []
                    : detail.attachments.filter(
                        (a) => a.instrumentTaskDataFieldId === dfl.instrumentTaskDataFieldId,
                      )
                return (
                  <div
                    key={dfl.id}
                    className={[
                      'rounded px-2 py-1',
                      isOutOfRange ? 'bg-red-100 text-red-700' : 'bg-muted',
                    ].join(' ')}
                  >
                    <div className="flex flex-wrap items-baseline gap-x-1.5">
                      <span className="font-medium">{dfl.fieldLabel ?? '—'}:</span>
                      {dfl.isConfidentialHidden ? (
                        <span
                          className="text-muted-foreground"
                          title={t('common.confidentialData')}
                        >
                          {CONFIDENTIAL_MASK}
                        </span>
                      ) : (
                        <>
                          <span className={isOutOfRange ? 'font-medium' : ''}>
                            {(dfl.fieldTypeCode === 'defined'
                              ? dfl.value?.toLowerCase()
                              : dfl.value) ?? '—'}
                          </span>
                          {dfl.validationRange && (
                            <span className="text-muted-foreground text-xs">
                              ({t('instruments.reports.logDetails.acceptableRange')}:{' '}
                              {dfl.validationRange})
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {fieldAttachments.length > 0 && (
                      <div className="mt-1 space-y-1 text-sm">
                        {fieldAttachments.map((attachment) => (
                          <AttachmentRow key={attachment.id} attachment={attachment} />
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Attachments not tied to a rendered data field (generic task-log uploads, or an
                NPT log where data fields are hidden) — never drop a file silently. */}
          {unpairedAttachments.length > 0 && (
            <div className="space-y-1 rounded border px-3 py-2">
              <h3 className="text-sm font-semibold">
                {t('instruments.reports.logDetails.attachments')}
              </h3>
              {unpairedAttachments.map((attachment) => (
                <AttachmentRow key={attachment.id} attachment={attachment} />
              ))}
            </div>
          )}

          {/* Supply buckets — Supplies (current) + Supplies Used */}
          {!detail.isNpt && <LotBucketsSection detail={detail} />}

          {/* Show the block only when there is something to show — a reason or an old -> new
                diff. A bare `date_modified` flag (e.g. a backdated-on-create seed log with no
                captured original and no reason) would otherwise render an empty "—" block. */}
          {detail.dateModified && (detail.modReason || detail.originalCompletedAtLocal) && (
            <div className="space-y-1 rounded border px-3 py-2">
              <h3 className="text-sm font-semibold">
                {t('instruments.reports.logDetails.dateModifiedReason')}
              </h3>
              {detail.modReason && <CommentBody body={detail.modReason} />}
              {/* AC6 (REBUILD-146): show the old -> new date diff when the original value was
                    captured. Absent for historical logs edited before the value was tracked. */}
              {detail.originalCompletedAtLocal && (
                <div className="pt-1 text-sm">
                  <p>
                    <span className="font-medium text-muted-foreground">
                      {t('instruments.reports.logDetails.dateChangedOriginal')}:
                    </span>{' '}
                    <span className="line-through text-muted-foreground">
                      {formatWallClockDisplay(detail.originalCompletedAtLocal)}
                    </span>
                  </p>
                  <p>
                    <span className="font-medium text-muted-foreground">
                      {t('instruments.reports.logDetails.dateChangedNew')}:
                    </span>{' '}
                    {formatWallClockDisplay(detail.completedAtLocal)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Comments — log comment + discussion comments unified (failures included) */}
          {(detail.taskLogComments || detail.comments.length > 0) && (
            <div
              className="space-y-1 rounded border px-3 py-2"
              data-testid="log-details-comments-block"
            >
              <h3 className="text-sm font-semibold">
                {t('instruments.reports.logDetails.comments')}:
              </h3>

              {detail.taskLogComments && (
                <div className="py-1" data-testid="log-details-task-log-comments">
                  <CommentBody body={detail.taskLogComments} />
                </div>
              )}

              {detail.comments.length > 0 && (
                <CommentsPanel
                  comments={detail.comments}
                  commentableType="InstrumentTaskLog"
                  commentableId={detail.id}
                  logAuthenticationCode={commentLogging.logAuthenticationCode}
                  loggerOptions={commentLogging.loggerOptions}
                  instrumentId={commentLogging.instrumentId}
                  onCommentChange={onCommentChange}
                  compact
                  hideTitle
                  hideForm={!showComments}
                />
              )}
            </div>
          )}

          {/* Add comment form when no existing comments */}
          {detail.comments.length === 0 && showComments && (
            <div className="rounded border px-3 py-2">
              <CommentsPanel
                comments={detail.comments}
                commentableType="InstrumentTaskLog"
                commentableId={detail.id}
                logAuthenticationCode={commentLogging.logAuthenticationCode}
                loggerOptions={commentLogging.loggerOptions}
                instrumentId={commentLogging.instrumentId}
                onCommentChange={onCommentChange}
                compact
                hideTitle
              />
            </div>
          )}

          {detail.dateModified && (
            <p>
              <span className="font-semibold">
                {t('instruments.reports.logDetails.completedBy')}
              </span>{' '}
              {detail.completedByName ?? '—'}
              {detail.completedByUserId != null ? ` (ID:${detail.completedByUserId})` : ''}{' '}
              {t('instruments.reports.logDetails.completedByOn')}{' '}
              {formatWallClockDisplay(detail.loggedAtLocal)}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default LogDetailsDialog
